import { Fragment, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import ReactMarkdown from "react-markdown";
import { api, type Deal, type User } from "@/lib/api";
import { AnimatedList } from "@/components/ui/animated-list";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, Calendar, Copy, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { lockBodyScroll, unlockBodyScroll } from "@/lib/bodyScrollLock";

const reportMdComponents: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  h1: ({ children, ...props }) => (
    <h1 className="text-xl font-semibold mt-4 mb-2 first:mt-0 border-b border-border/60 pb-1" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="text-lg font-semibold mt-4 mb-2 first:mt-0 border-b border-border/40 pb-0.5" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-base font-semibold mt-3 mb-1.5 first:mt-0" {...props}>{children}</h3>
  ),
  p: ({ children, ...props }) => (
    <p className="text-sm text-foreground/90 leading-relaxed my-2 first:mt-0 last:mb-0" {...props}>{children}</p>
  ),
  ul: ({ children, ...props }) => (
    <ul className="list-disc list-inside text-sm text-foreground/90 my-2 space-y-0.5 pl-1" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="list-decimal list-inside text-sm text-foreground/90 my-2 space-y-0.5 pl-1" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }) => (
    <li className="leading-relaxed" {...props}>{children}</li>
  ),
  code: ({ className, children, ...props }) => {
    const isBlock = className?.includes("language-");
    return (
      <code
        className={cn(
          "font-mono",
          isBlock ? "text-xs block py-0" : "text-xs bg-muted/50 rounded px-1.5 py-0.5 border border-border/30"
        )}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre className="text-xs bg-muted/50 rounded-lg p-3 border border-border/40 overflow-x-auto my-2 font-mono whitespace-pre" {...props}>{children}</pre>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote className="border-l-4 border-primary/40 pl-3 my-2 text-sm text-muted-foreground italic" {...props}>{children}</blockquote>
  ),
  a: ({ children, href, ...props }) => (
    <a href={href} className="text-primary underline underline-offset-2 hover:opacity-80" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-foreground" {...props}>{children}</strong>
  ),
  em: ({ children, ...props }) => (
    <em className="italic" {...props}>{children}</em>
  ),
  hr: () => <hr className="border-border/60 my-4" />,
};

function ReportMarkdown({ content }: { content: string }) {
  return (
    <div className="report-md">
      <ReactMarkdown components={reportMdComponents}>{content}</ReactMarkdown>
    </div>
  );
}

function parseDateParts(iso: string) {
  const d = new Date(iso);
  return { y: d.getFullYear(), m: d.getMonth() + 1, d: d.getDate() };
}

function useLgUp() {
  const [lgUp, setLgUp] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const onChange = () => setLgUp(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return lgUp;
}

function ReportDetailInner({
  deal,
  copiedDealId,
  onCopy,
}: {
  deal: Deal;
  copiedDealId: string | null;
  onCopy: (d: Deal) => void;
}) {
  return (
    <>
      <div className="p-4 border-b border-border/60">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary/10 p-2 shrink-0">
            <Calendar className="size-4 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium">{deal.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {new Date(deal.endAt).toLocaleString("ja-JP")}
            </p>
            {deal.fixedReport && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mt-2">
                <FileText className="size-3.5" /> 報告済
              </span>
            )}
          </div>
          {(deal.fixedReport ?? deal.rawReport) && (
            <button
              type="button"
              onClick={() => onCopy(deal)}
              className={cn(
                "shrink-0 rounded-lg p-2 transition-colors",
                copiedDealId === deal.id
                  ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                  : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
              )}
              title="報告書をコピー"
            >
              <span className="flex items-center gap-1.5">
                {copiedDealId === deal.id ? (
                  <Check className="size-4 shrink-0" aria-hidden />
                ) : (
                  <Copy className="size-4 shrink-0" aria-hidden />
                )}
                <span>{copiedDealId === deal.id ? "コピーしました" : "コピー"}</span>
              </span>
            </button>
          )}
        </div>
      </div>
      <div className="p-4 flex-1 overflow-auto">
        {deal.fixedReport ? (
          <ReportMarkdown content={deal.fixedReport} />
        ) : deal.rawReport ? (
          <p className="text-sm text-muted-foreground">
            整形中…<br />
            <span className="text-foreground/80 mt-2 block">生メモ:</span>
            <pre className="whitespace-pre-wrap mt-1 text-xs bg-muted/30 rounded p-3 border border-border/40">
              {deal.rawReport}
            </pre>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            報告・ヒアリングメモはまだありません
          </p>
        )}
      </div>
    </>
  );
}

export function DashboardPage() {
  const { company } = useAuth();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [filterYear, setFilterYear] = useState<string>("");
  const [filterMonth, setFilterMonth] = useState<string>("");
  const [filterDay, setFilterDay] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");
  const [copiedDealId, setCopiedDealId] = useState<string | null>(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const lgUp = useLgUp();

  async function copyReportContent(deal: Deal) {
    const text = deal.fixedReport ?? deal.rawReport ?? "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedDealId(deal.id);
      setTimeout(() => setCopiedDealId(null), 2000);
    } catch {
      // clipboard API が使えない環境では無視
    }
  }

  useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    api.users.listByCompany(company.id).then(setMembers).catch(() => setMembers([]));
    api.deals
      .list({ companyId: company.id, userId: selectedUserId || undefined })
      .then(setDeals)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [company?.id, selectedUserId]);

  const filteredAndSortedDeals = useMemo(() => {
    let list = deals.filter(deal => {
      const parts = parseDateParts(deal.endAt);
      if (filterYear && parts.y !== Number(filterYear)) return false;
      if (filterMonth && parts.m !== Number(filterMonth)) return false;
      if (filterDay && parts.d !== Number(filterDay)) return false;
      return true;
    });
    const order = sortOrder === "desc" ? 1 : -1;
    return [...list].sort(
      (a, b) => order * (new Date(b.endAt).getTime() - new Date(a.endAt).getTime())
    );
  }, [deals, selectedUserId, filterYear, filterMonth, filterDay, sortOrder]);

  const yearOptions = useMemo(() => {
    const years = new Set(deals.map(d => parseDateParts(d.endAt).y));
    if (years.size === 0) years.add(new Date().getFullYear());
    return Array.from(years).sort((a, b) => b - a);
  }, [deals]);

  useEffect(() => {
    if (selectedDeal && !filteredAndSortedDeals.some(d => d.id === selectedDeal.id)) {
      setSelectedDeal(null);
    }
  }, [filteredAndSortedDeals, selectedDeal]);

  useEffect(() => {
    if (lgUp) setMobileDetailOpen(false);
  }, [lgUp]);

  useEffect(() => {
    const lockId = "dashboard:mobile-detail";
    const shouldLock = !lgUp && mobileDetailOpen && !!selectedDeal;
    if (shouldLock) lockBodyScroll(lockId);
    else unlockBodyScroll(lockId);
    return () => unlockBodyScroll(lockId);
  }, [lgUp, mobileDetailOpen, selectedDeal]);

  function handleSelectDeal(deal: Deal) {
    setSelectedDeal(deal);
    if (!lgUp) setMobileDetailOpen(true);
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">業務報告書一覧</h2>
        <p className="text-muted-foreground text-sm mt-1">商談一覧と報告書を確認</p>
      </div>

      {error && (
        <p className="text-destructive text-sm">{error}</p>
      )}

      {loading && !deals.length ? (
        <p className="text-muted-foreground text-sm">読み込み中…</p>
      ) : company ? (
        <div className="min-h-0">
          <div className="lg:grid lg:grid-cols-[2fr_1fr] lg:gap-8 min-h-0">
            {/* 左カラム(デスクトップのみ): 選択中の報告書詳細 */}
            <div className="hidden lg:flex min-w-0 flex-col">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">選択中の報告書</h3>
              <AnimatePresence mode="wait">
                {selectedDeal ? (
                  <motion.div
                    key={selectedDeal.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    transition={{ duration: 0.2 }}
                    className="rounded-xl border border-border/60 bg-card/50 overflow-hidden flex-1 min-h-0 flex flex-col"
                  >
                    <ReportDetailInner deal={selectedDeal} copiedDealId={copiedDealId} onCopy={copyReportContent} />
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="rounded-xl border border-dashed border-border/60 bg-muted/20 flex-1 flex items-center justify-center min-h-[200px]"
                  >
                    <p className="text-sm text-muted-foreground">右の一覧から報告書を選択してください</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* 右カラム: AnimatedList で一覧 (モバイルではこれだけ常時表示) */}
            <div className="min-w-0 flex flex-col">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <h3 className="text-sm font-medium text-muted-foreground shrink-0">
                {company.name} の商談
              </h3>
              <Select value={selectedUserId || "all"} onValueChange={v => setSelectedUserId(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[200px]" size="sm">
                  <SelectValue placeholder="メンバー" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全員</SelectItem>
                  {members.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Select value={filterYear || "all"} onValueChange={v => setFilterYear(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[88px]" size="sm">
                  <SelectValue placeholder="年" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">年</SelectItem>
                  {yearOptions.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}年</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterMonth || "all"} onValueChange={v => setFilterMonth(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[72px]" size="sm">
                  <SelectValue placeholder="月" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">月</SelectItem>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <SelectItem key={m} value={String(m)}>{m}月</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterDay || "all"} onValueChange={v => setFilterDay(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[72px]" size="sm">
                  <SelectValue placeholder="日" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">日</SelectItem>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                    <SelectItem key={d} value={String(d)}>{d}日</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sortOrder} onValueChange={v => setSortOrder(v as "desc" | "asc")}>
                <SelectTrigger className="w-[120px]" size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">新しい順</SelectItem>
                  <SelectItem value="asc">古い順</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-h-0 overflow-auto">
              {filteredAndSortedDeals.length === 0 ? (
                <p className="text-muted-foreground text-sm py-8 text-center">
                  商談がありません
                </p>
              ) : (
                <AnimatedList delay={60} className="items-stretch gap-3">
                  {filteredAndSortedDeals.map((deal) => (
                    <button
                      key={deal.id}
                      type="button"
                      onClick={() => handleSelectDeal(deal)}
                      className={cn(
                        "w-full flex items-center gap-4 p-4 text-left rounded-xl border transition-colors",
                        "hover:bg-muted/30 hover:border-border",
                        selectedDeal?.id === deal.id
                          ? "bg-primary/10 border-primary/30 ring-1 ring-primary/20"
                          : "border-border/60 bg-card/50"
                      )}
                    >
                      <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                        <Calendar className="size-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                        <p className="font-medium truncate">{deal.title}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                          {new Date(deal.endAt).toLocaleString("ja-JP")}
                          {deal.fixedReport && (
                            <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-400">
                              報告済
                            </span>
                          )}
                        </p>
                      </div>
                    </button>
                  ))}
                </AnimatedList>
              )}
            </div>
          </div>
          </div>

          {/* モバイル: 選択中の詳細を下から出す */}
          <AnimatePresence>
            {!lgUp && mobileDetailOpen && selectedDeal && (
              <Fragment key="mobile-detail">
                <motion.div
                  role="presentation"
                  className="fixed inset-0 z-50 bg-black/50 lg:hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => setMobileDetailOpen(false)}
                />
                <motion.div
                  role="dialog"
                  aria-modal="true"
                  className="fixed inset-x-0 bottom-0 z-50 lg:hidden h-[80vh] bg-background border-t border-border/70 rounded-t-2xl shadow-2xl flex flex-col overflow-hidden"
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "tween", duration: 0.18 }}
                >
                  <div className="px-4 pt-3 pb-2 border-b border-border/60 bg-background/90 backdrop-blur">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="mx-auto h-1.5 w-10 rounded-full bg-muted" />
                        <p className="text-xs text-muted-foreground mt-2">選択中の報告書</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setMobileDetailOpen(false)}
                        className="shrink-0 rounded-lg p-2 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                        aria-label="閉じる"
                      >
                        <X className="size-5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-auto">
                    <ReportDetailInner deal={selectedDeal} copiedDealId={copiedDealId} onCopy={copyReportContent} />
                  </div>
                </motion.div>
              </Fragment>
            )}
          </AnimatePresence>
        </div>
      ) : (
        !loading && (
          <p className="text-muted-foreground text-sm">会社情報を読み込めませんでした</p>
        )
      )}
    </div>
  );
}
